import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'cityads-v3/i18n';

import { setLanguage } from 'cityads-v3/actions/language';
import { RootState } from 'cityads-v3/types/reducer';

import Emoji from 'cityads-v3/components/Emoji/Emoji'
import Box, { BoxProps } from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import MenuPopper from 'cityads-v3/components/MenuPopper/MenuPopper'
import Typography from '@mui/material/Typography'

import classes from './HeaderLanguageMenu.scss';

type HeaderLanguageMenuProps = BoxProps

export default function HeaderLanguageMenu({ ...rest }: HeaderLanguageMenuProps) {
  const ref = useRef()
  const dispatch = useDispatch()
  const [open, setOpen] = useState<boolean>(false)
  const changingLanguageTo = useSelector((state: RootState) => state.language.changingLanguageTo)
  const language = useSelector((state: RootState) => state.language.language)
  const languageList = useSelector((state: RootState) => state.language.languageList)
  const languagesById = useSelector((state: RootState) => state.language.languagesById)

  const handleClickLanguage = useCallback((language: string) => {
    setOpen(false)
    dispatch(setLanguage({ language, i18n }));
  }, [dispatch])

  const showLanguageMenu = useCallback(() => setOpen(true), [])
  const hideLanguageMenu = useCallback(() => setOpen(false), [])

  const languageItems = useMemo(() => languageList.map(lang => languagesById[lang]).map(language => (
    <MenuItem
      key={language.id}
      value={language.id}
      onClick={(evt) => handleClickLanguage(language.id)}
    >
      {language.flag && <span className={classes.flag}><Emoji icon={true} size={24} code={`:flag_${language.flag}:`} /></span>}
      {language.name}
    </MenuItem>
  )), [handleClickLanguage, languageList, languagesById])

  return (
    <Box {...rest}>
      <Typography
        component="div"
        ref={ref}
        onClick={showLanguageMenu}
        color="textPrimary"
        variant="caption"
      >
        {String(changingLanguageTo ?? language).toUpperCase()}
      </Typography>
      <MenuPopper
        className={classes.languagePopover}
        open={open}
        anchorEl={ref.current}
        placement={'bottom-end'}
        onClose={hideLanguageMenu}
        MenuListProps={{
          sx: {
            boxShadow: 'rgba(0, 0, 0, 0.3) 0px 19px 60px, rgba(0, 0, 0, 0.22) 0px 15px 20px'
          }
        }}
      >
        {languageItems}
      </MenuPopper>
    </Box>
  )
}
